import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import MediaCoverage from '../components/MediaCoverage';
import { 
  Building2, CheckCircle, Users, TrendingUp, Award, 
  Eye, Share2, GraduationCap, Heart, Calendar, Mail,
  Star, BadgeCheck, Target, Newspaper, Quote,
  ArrowRight, Sparkles, ExternalLink
} from 'lucide-react';

export function AgregarCentroPage() {
  const estadisticas = [
    { valor: '+500', label: 'Citas agendadas', icon: Calendar },
    { valor: '+30.000', label: 'Visitas mensuales', icon: Eye },
    { valor: '+5.000', label: 'Suscriptores', icon: Mail },
    { valor: '+200', label: 'Centros verificados', icon: BadgeCheck }
  ];

  const beneficios = [
    {
      icon: Award,
      title: 'El ÚNICO Directorio de Chile',
      description: 'Sé parte del primer y único directorio 100% enfocado en centros de rehabilitación de adicciones en Chile.'
    },
    {
      icon: Users,
      title: 'Público Cualificado',
      description: 'Nuestros visitantes son familias y personas buscando activamente tratamiento para dependencias. Pacientes reales que necesitan tu centro.'
    },
    {
      icon: Eye,
      title: '+30.000 Visitas Mensuales',
      description: 'Miles de familias buscan ayuda cada mes en nuestro sitio. Tu centro será visible para quienes más lo necesitan.'
    },
    {
      icon: Share2,
      title: '+25.000 Seguidores en Redes',
      description: 'Publicamos a los centros asociados en Instagram y Facebook, llegando a miles de familias que buscan opciones.'
    },
    {
      icon: Heart,
      title: 'Creado por Personas en Recuperación',
      description: 'Nuestra plataforma fue construida por quienes vivieron la adicción. Entendemos a tus pacientes porque fuimos pacientes.'
    },
    {
      icon: GraduationCap,
      title: '20% Descuento en Formación',
      description: 'Acceso exclusivo a descuentos en el Máster en Adicciones con Academia Lidera para tu equipo profesional.'
    }
  ];

  const pasos = [
    { num: '1', title: 'Regístrate', desc: 'Crea tu cuenta en menos de 2 minutos' },
    { num: '2', title: 'Completa tu perfil', desc: 'Agrega fotos, servicios y descripción' },
    { num: '3', title: 'Verifica tu centro', desc: 'Obtén el badge de verificación' },
    { num: '4', title: '¡Listo!', desc: 'Comienza a recibir consultas' }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Agregar Centro de Rehabilitación | SinAdicciones.org - El Único Directorio de Chile</title>
        <meta name="description" content="Registra tu centro de rehabilitación en el único directorio especializado en adicciones de Chile. +30.000 visitas mensuales, +500 citas agendadas. Conecta con familias que buscan ayuda." />
        <meta name="keywords" content="agregar centro rehabilitación, registrar clínica adicciones, directorio centros Chile, tratamiento drogas alcohol" />
        <link rel="canonical" href="https://sinadicciones.org/agregar-centro" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/agregar-centro" />
        <meta property="og:title" content="Agregar Centro de Rehabilitación | El Único Directorio de Chile" />
        <meta property="og:description" content="Registra tu centro en el único directorio especializado en adicciones de Chile. +30.000 visitas mensuales, +500 citas agendadas." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agregar Centro de Rehabilitación | SinAdicciones.org" />
        <meta name="twitter:description" content="Registra tu centro. +30.000 visitas/mes." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      

      <Header />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-slate-900 to-slate-950"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          
          <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full mb-6">
                <Award className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-semibold">EL ÚNICO EN CHILE</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 mb-6 leading-tight">
                El Único Directorio de<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                  Centros de Rehabilitación
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-4">
                Registra tu centro en el directorio más consultado de Chile para tratamiento de adicciones.
              </p>
              
              <p className="text-lg text-slate-400 mb-8">
                Nuestro público: <strong className="text-emerald-400">Familias y personas</strong> buscando activamente 
                tratamiento para dependencias a sustancias y comportamentales.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
                >
                  <Link to="/login">
                    <Building2 className="w-5 h-5 mr-2" />
                    Registrar mi Centro
                  </Link>
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-slate-400 flex-wrap">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Registro gratuito
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Verificación incluida
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Publicidad en redes
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Estadísticas */}
        <section className="py-12 bg-slate-900/50 border-y border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {estadisticas.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <p className="text-3xl md:text-4xl font-bold text-slate-50 mb-1">{stat.valor}</p>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                ¿Por qué registrar tu centro en <span className="text-emerald-400">SinAdicciones</span>?
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Beneficios exclusivos para centros del directorio
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {beneficios.map((b, idx) => {
                const Icon = b.icon;
                return (
                  <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all group">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                        <Icon className="w-7 h-7 text-emerald-400" />
                      </div>
                      <h3 className="font-semibold text-slate-50 text-lg mb-2">{b.title}</h3>
                      <p className="text-slate-400">{b.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Nuestra Misión */}
        <section className="py-20 bg-gradient-to-b from-slate-900/50 to-slate-950 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                Nuestra <span className="text-emerald-400">Misión</span>
              </h2>
            </div>
            
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 text-center">
              <Quote className="w-10 h-10 text-emerald-500/30 mx-auto mb-4" />
              <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-6 italic">
                "Acortar el camino entre la familia que busca ayuda y el lugar correcto para encontrarla, 
                con información verificada, orientación honesta y la credibilidad de quienes lo vivieron desde adentro."
              </p>
              <p className="text-slate-400">
                Tu centro puede ser ese lugar correcto para alguien que hoy está buscando.
              </p>
            </div>
          </div>
        </section>

        {/* Lo que dicen los medios */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/40 rounded-full mb-4">
                <Newspaper className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 font-semibold">EN LOS MEDIOS</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                Lo que Dicen de <span className="text-blue-400">Nosotros</span>
              </h2>
            </div>

            <MediaCoverage />
          </div>
        </section>
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-semibold">PLANES Y PRECIOS</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-50 mb-4">
                Elige el Plan para tu <span className="text-emerald-400">Centro</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Aumenta tu visibilidad y recibe más pacientes. Planes adaptados a diferentes necesidades.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
              {/* Gratuito */}
              <Card className="bg-slate-900 border-slate-700">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-300 mb-1">Gratuito</h3>
                  <p className="text-xs text-slate-500 mb-3">Presencia básica</p>
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-slate-400">$0</span>
                    <span className="text-slate-500 text-sm"> / mes</span>
                  </div>
                  <ul className="space-y-1.5 text-xs text-slate-400">
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Perfil del centro</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Recibir reseñas</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Destacado */}
              <Card className="bg-gradient-to-br from-blue-500/10 to-slate-900 border-blue-500/30">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-blue-400 mb-1">Destacado</h3>
                  <p className="text-xs text-slate-400 mb-3">Mayor visibilidad · Top 11-20</p>
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-slate-50">$99.000</span>
                    <span className="text-slate-400 text-sm"> / mes</span>
                  </div>
                  <ul className="space-y-1.5 text-xs text-slate-300">
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Badge "Destacado"</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Posición Top 20</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Premium */}
              <Card className="bg-gradient-to-br from-amber-500/10 to-slate-900 border-amber-500/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-amber-400 mb-1">Premium</h3>
                  <p className="text-xs text-slate-400 mb-3">Máxima visibilidad · Top 4-10</p>
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-slate-50">$199.000</span>
                    <span className="text-slate-400 text-sm"> / mes</span>
                  </div>
                  <ul className="space-y-1.5 text-xs text-slate-300">
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Badge "Premium"</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Prioridad TerapeutAI</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Elite */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-slate-900 border-purple-500/30">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-purple-400 mb-1">Elite</h3>
                  <p className="text-xs text-slate-400 mb-3">Top 1-3 nacional</p>
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-slate-50">$350.000</span>
                    <span className="text-slate-400 text-sm"> / mes</span>
                  </div>
                  <ul className="space-y-1.5 text-xs text-slate-300">
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Badge "Elite"</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Soporte VIP 24/7</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Link to="/planes">Ver todos los planes en detalle <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Pasos */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-50 mb-4">
                Comienza en <span className="text-emerald-400">4 Pasos</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {pasos.map((p, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    {p.num}
                  </div>
                  <h4 className="font-semibold text-slate-50 mb-1">{p.title}</h4>
                  <p className="text-sm text-slate-400">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <Building2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
              Únete al <span className="text-emerald-400">Único Directorio</span> de Adicciones en Chile
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Tu centro puede ser la respuesta que una familia está buscando hoy. 
              Conecta con quienes realmente necesitan tu ayuda.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl px-10 py-6 text-lg font-semibold shadow-lg"
            >
              <Link to="/login">
                Registrar mi Centro
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <p className="text-sm text-slate-500 mt-4">
              Sin tarjeta de crédito • Verificación gratuita • +500 citas agendadas
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default AgregarCentroPage;
